// ── All Contacts — global list, PORTFOLIO app ──
//
// Asks for scope=portfolio, so this list carries only contacts on accounts the
// portfolio app can see — the dbo.LifecycleStages gate, the same one behind the
// account list and the dashboard. A prospect's contacts belong in Sales and are
// not part of the servicing book.
//
// The Sales contacts page deliberately does NOT pass it: a prospect that is not
// portfolio-visible is exactly the record a rep is working, and filtering it
// out there would empty the page.
import { useState, useEffect } from "react";
import { localFilter, exportCSV, fmtPhone } from "../../../shared/format";
import { Spinner, ErrorMsg, SectionHeader, SearchBar, Btn, Card, STh, Th, Td, Icon, Pagination } from "../../../shared/ui";
import { useSortable, usePagination } from "../../../shared/hooks";
import { useApp } from "../../../shared/appContext";
import { PortalBadge, portalState } from "../../../shared/portalAccess";

export const AllContactsPage = () => {
      const { api, setModal, role, setConfirm, delRec } = useApp();
      const [rows,setRows]=useState([]); const [loading,setLoading]=useState(true); const [err,setErr]=useState(null);
      const [q,setQ]=useState("");
      const [onlyPending,setOnlyPending]=useState(false);
      // Three text columns stamped onto the row so each sorts, searches and
      // exports like any other field. A raw bit sorts as 0/1 and searches as
      // nothing at all, so typing "yes" or "pending" would find neither.
      //
      //   invoice  the stored Invoice Delivery bit — who gets the notice
      //   flag     the stored Portal Access bit — what somebody INTENDED
      //   portal   the derived login state — what is actually TRUE
      //
      // flag and portal are deliberately both here and deliberately adjacent.
      // They are different facts and the gap between them is the worklist; a
      // single merged column would hide exactly the thing worth seeing.
      const load=()=>{setLoading(true);api.get("getContacts",{scope:"portfolio"}).then(d=>{
        setRows((d||[]).map(c=>({ ...c,
          invoice: c.invoicedelivery ? "Yes" : "No",
          flag:    c.portalaccess    ? "Yes" : "No",
          portal:  portalState(c).label,
          news:    c.monitornews ? "Yes" : "No" })));
        setLoading(false);}).catch(e=>{setErr(e.message);setLoading(false);});};
      useEffect(()=>{load();},[]);
      const pending=rows.filter(c=>portalState(c).key==="pending");
      // Counted apart from the worklist on purpose — these cannot be actioned
      // today, because the portal holds one login per email address and these
      // people already have one on another borrower.
      const blocked=rows.filter(c=>portalState(c).key==="blocked");
      // Flags left behind at borrowers that paid off or terminated. Counted
      // apart from the worklist because there is nothing to set up — the
      // relationship ended. Shown at all only so the number is known and can
      // be worked down, rather than quietly inflating the worklist forever.
      const stale=rows.filter(c=>portalState(c).key==="stale");
      // The security direction: still able to sign in at a closed borrower.
      const offboard=rows.filter(c=>portalState(c).key==="offboard");
      const base=onlyPending?pending:rows;
      const filtered=localFilter(base,q,["firstname","lastname","email","companyname","title","phone","mobilephone","portal"]);
      // invoice and flag are left OUT of the search fields on purpose: every
      // contact is a "Yes" or a "No", so including them would make those two
      // words match the whole book and stop the search box being useful.
      const { sorted, sortKey, sortDir, toggle } = useSortable(filtered, 'lastname');
      const { paginated:displayed, page, setPage, totalPages, total, reset } = usePagination(sorted, 100);

      if(loading) return <Spinner />; if(err) return <ErrorMsg msg={err} onRetry={load} />;
      return <>
        <SectionHeader actions={[
          <SearchBar key="s" value={q} onChange={v=>{ setQ(v); reset(); }} placeholder="Search contacts..." />,
          <Btn key="ex" onClick={()=>exportCSV(sorted,["firstname","lastname","title","email","phone","mobilephone","companyname","invoice","flag","portal",...(role.canViewMonitoring?["news"]:[])],"contacts.csv")}>Export CSV</Btn>
        ]} />
        <div style={{ fontSize:11.5, color:"var(--color-text-secondary)", marginBottom:10, lineHeight:1.5 }}>
          Contacts on accounts visible to the portfolio app. A contact on a prospect or a lost deal
          is in the Sales app, not here — the account has to reach a portfolio-visible lifecycle
          stage before its people appear.
        </div>
        {/* The worklist. Portal Access is a legacy flag meaning "should have
            access"; the login is whether they actually do. The gap between the
            two is a list of people to set up, and it is invisible unless
            something counts it — so this counts it. */}
        {/* First, because it is the only one with a security cost. */}
        {offboard.length>0 && (
          <div style={{ fontSize:12, background:"#FBE4DD", color:"#993C1D", border:"1px solid #F0C4B6",
                        borderRadius:6, padding:"8px 12px", marginBottom:12, lineHeight:1.5 }}>
            <b>{offboard.length}</b> {offboard.length===1?"person":"people"} can still sign in to the
            client portal at a borrower that has paid off or terminated. Deactivate
            {offboard.length===1?" the login":" those logins"} in Client Portal Login.
          </div>
        )}
        {pending.length>0 && (
          <div style={{ fontSize:12, background:"#FDF0D5", color:"#7A5200", border:"1px solid #EBD9A8",
                        borderRadius:6, padding:"8px 12px", marginBottom:12, display:"flex",
                        alignItems:"center", gap:12, lineHeight:1.5 }}>
            <span>
              <b>{pending.length}</b> {pending.length===1?"contact is":"contacts are"} flagged for
              portal access but {pending.length===1?"has":"have"} no login yet. The flag is the
              intent; the login is the fact. Set them up in Client Portal Login.
            </span>
            <Btn onClick={()=>{ setOnlyPending(!onlyPending); reset(); }}>
              {onlyPending?"Show all contacts":"Show only these"}
            </Btn>
          </div>
        )}
        {blocked.length>0 && (
          <div style={{ fontSize:12, background:"#FBE4DD", color:"#993C1D", border:"1px solid #F0C4B6",
                        borderRadius:6, padding:"8px 12px", marginBottom:12, lineHeight:1.5 }}>
            <b>{blocked.length}</b> {blocked.length===1?"contact is":"contacts are"} flagged here but
            already {blocked.length===1?"holds":"hold"} a portal login on another borrower. The portal
            allows one login per email address, so {blocked.length===1?"this one":"these"} cannot be set
            up until a login can cover more than one account.
          </div>
        )}
        {stale.length>0 && (
          <div style={{ fontSize:12, color:"var(--color-text-secondary)",
                        background:"var(--color-background-secondary)", borderRadius:6,
                        padding:"8px 12px", marginBottom:12, lineHeight:1.5 }}>
            <b>{stale.length}</b> {stale.length===1?"contact carries":"contacts carry"} a portal-access
            flag at a borrower that has paid off or terminated. Nothing to set up — these are
            leftovers from when they were clients, and are not counted in the list above.
          </div>
        )}
        <Card style={{ overflowX:"auto" }}>
          <table style={{ width:"100%",borderCollapse:"collapse", whiteSpace:"nowrap" }}>
            <thead><tr>
              <STh label="First Name" sortKey="firstname"   currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
              <STh label="Last Name"  sortKey="lastname"    currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
              <STh label="Title"      sortKey="title"       currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
              <STh label="Email"      sortKey="email"       currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
              <STh label="Phone"      sortKey="phone"       currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
              <STh label="Mobile"     sortKey="mobilephone" currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
              <STh label="Account"    sortKey="companyname" currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
              <STh label="Invoice Delivery" sortKey="invoice" currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
              <STh label="Portal Access"    sortKey="flag"    currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
              {/* Renamed from "Portal" now that the stored bit sits beside it —
                  two columns called Portal-something would be a coin toss. */}
              <STh label="Portal Login"     sortKey="portal"  currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
              {/* News Monitor: the contact's "Monitor news" box. Portfolio managers and admins only. */}
              {role.canViewMonitoring && <STh label="News" sortKey="news" currentKey={sortKey} currentDir={sortDir} onSort={toggle} />}
              <Th></Th>
            </tr></thead>
            <tbody>{displayed.map(c=>(
              <tr key={c.id} style={{ cursor:"pointer" }} onClick={()=>setModal({type:"contact-view",rec:{...c,id:c.id,accountid:c.accountid}})} onMouseEnter={e=>e.currentTarget.style.background="var(--color-background-secondary)"} onMouseLeave={e=>e.currentTarget.style.background=""}>
                <Td>{c.firstname||"—"}</Td>
                <Td style={{ fontWeight:500 }}>{c.lastname||"—"}</Td>
                <Td>{c.title||"—"}</Td>
                <Td style={{ color:"var(--color-text-secondary)" }}>{c.email||"—"}</Td>
                <Td>{fmtPhone(c.phone)}</Td>
                <Td>{fmtPhone(c.mobilephone)}</Td>
                <Td>{c.companyname||"—"}</Td>
                {/* Ticks, matching how the account page has always shown these
                    two. An em dash rather than an empty cell, so "off" is
                    visibly off and not mistaken for missing data. */}
                <Td>{c.invoicedelivery?"✓":"—"}</Td>
                <Td>{c.portalaccess?"✓":"—"}</Td>
                <Td><PortalBadge contact={c} /></Td>
                {role.canViewMonitoring && <Td>{c.monitornews ? <span title="Searched daily on News Monitor" style={{ fontSize:11, fontWeight:600, padding:"2px 8px", borderRadius:10, background:"#E6F7FC", color:"#116588", whiteSpace:"nowrap" }}>Monitored</span> : "—"}</Td>}
                <Td><div style={{ display:"flex",gap:6, justifyContent:"flex-end" }} onClick={e=>e.stopPropagation()}>
                  <Btn onClick={()=>setModal({type:"contact-view",rec:{...c,id:c.id,accountid:c.accountid}})}><Icon type="view" /></Btn>
                  {role.canEdit&&<Btn onClick={()=>setModal({type:"contact-edit",rec:{...c,id:c.id,accountid:c.accountid},onDone:load})}><Icon type="edit" /></Btn>}
                  {role.canDelete&&<Btn onClick={()=>setConfirm({msg:"Delete contact "+c.firstname+" "+c.lastname+"?",onConfirm:()=>delRec("Contacts",c.id,"Deleted contact "+c.firstname+" "+c.lastname,load)})} variant="danger"><Icon type="delete" /></Btn>}
                </div></Td>
              </tr>
            ))}</tbody>
          </table>
          <Pagination page={page} totalPages={totalPages} total={total} pageSize={100} onPage={setPage} />
        </Card>
      </>;
    };
